/* eslint-disable no-underscore-dangle */
import {
  ElementNode,
  $createParagraphNode,
  RangeSelection,
  SerializedElementNode,
  Spread,
  EditorConfig,
  LexicalEditor,
  ParagraphNode,
  DOMConversionMap,
  $getRoot,
} from 'lexical';

import { $convertCodeBlockElement, $createCodeBlockNode } from './helpers';

export type SerializedCodeBlockNode = Spread<
  {
    version: 1;
    textFormat: number;
    textStyle: string;
  },
  SerializedElementNode
>;

export class CodeBlockNode extends ElementNode {
  createDOM(_config: EditorConfig, editor: LexicalEditor): HTMLElement {
    const isEditable = editor.isEditable();
    const div = document.createElement('div');
    div.classList.add('editor-code-block');

    if (isEditable) {
      this.addDeleteButton(div, editor);
      this.updatePlaceholder(div, editor);
    }

    return div;
  }

  addDeleteButton(container: HTMLElement, editor: LexicalEditor): void {
    const tooltipContainer = document.createElement('span');
    tooltipContainer.classList.add('editor-delete-button-tooltip');
    const tooltipText = document.createElement('span');
    tooltipText.classList.add('editor-tooltip-text');
    tooltipText.textContent = 'Удалить';
    const deleteButton = document.createElement('button');
    deleteButton.classList.add(
      'editor-delete-button',
      'editor-delete-button-tooltip-toggle',
    );
    deleteButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.04148 3H1.75C1.33579 3 1 3.33579 1 3.75C1 4.16421 1.33579 4.5 1.75 4.5H2.29744L3.03566 11.6983C3.07233 12.0561 3.10416 12.3665 3.15066 12.6219C3.19999 12.8928 3.27483 13.1592 3.42741 13.4122C3.65663 13.7922 3.99309 14.0959 4.39449 14.2851C4.66168 14.4111 4.93432 14.4584 5.20888 14.4798C5.46769 14.5 5.77973 14.5 6.13935 14.5H9.8607C10.2203 14.5 10.5323 14.5 10.7911 14.4798C11.0657 14.4584 11.3383 14.4111 11.6055 14.2851C12.0069 14.0959 12.3434 13.7922 12.5726 13.4122C12.7252 13.1593 12.8 12.8929 12.8494 12.6219C12.8959 12.3665 12.9277 12.0562 12.9644 11.6984L13.7027 4.5H14.25C14.6642 4.5 15 4.16421 15 3.75C15 3.33579 14.6642 3 14.25 3H10.9585C10.7205 1.58114 9.4865 0.5 8 0.5C6.5135 0.5 5.27952 1.58114 5.04148 3ZM6.58535 3H9.41465C9.20873 2.4174 8.65311 2 8 2C7.34689 2 6.79127 2.4174 6.58535 3ZM12.1949 4.5H3.8053L4.52507 11.5183C4.56531 11.9108 4.59164 12.1623 4.6264 12.3532C4.65949 12.5349 4.69091 12.6027 4.71183 12.6374C4.78823 12.7641 4.90039 12.8653 5.03419 12.9284C5.07081 12.9456 5.14146 12.97 5.32562 12.9844C5.51905 12.9995 5.77198 13 6.16646 13H9.83358C10.2281 13 10.481 12.9995 10.6744 12.9844C10.8586 12.97 10.9292 12.9456 10.9658 12.9284C11.0996 12.8653 11.2118 12.7641 11.2882 12.6374C11.3091 12.6027 11.3405 12.5349 11.3736 12.3532C11.4084 12.1623 11.4347 11.9108 11.475 11.5184L12.1949 4.5ZM6.21255 5.50094C6.62624 5.48025 6.97838 5.79885 6.99906 6.21255L7.24906 11.2125C7.26975 11.6262 6.95115 11.9784 6.53745 11.9991C6.12376 12.0197 5.77162 11.7011 5.75094 11.2875L5.50094 6.28745C5.48025 5.87376 5.79885 5.52162 6.21255 5.50094ZM9.78745 5.50094C10.2011 5.52162 10.5197 5.87376 10.4991 6.28745L10.2491 11.2875C10.2284 11.7011 9.87624 12.0197 9.46255 11.9991C9.04885 11.9784 8.73025 11.6262 8.75094 11.2125L9.00094 6.21255C9.02162 5.79885 9.37376 5.48025 9.78745 5.50094Z" fill="#1E1E1E"/>
      </svg>
    `;
    tooltipContainer.appendChild(deleteButton);
    tooltipContainer.appendChild(tooltipText);

    const listener = () => {
      editor.update(() => {
        const node = this;
        if (node && node.isAttached()) {
          const root = $getRoot();
          if (root.getChildrenSize() === 0) {
            $createParagraphNode().select();
          } else {
            root.selectEnd();
          }

          deleteButton.removeEventListener('click', listener);
          node.remove();
        }
      });
    };

    deleteButton.addEventListener('click', (e) => {
      e.stopPropagation();

      listener();
    });

    container.appendChild(tooltipContainer);
  }

  updatePlaceholder(dom: HTMLElement, editor?: LexicalEditor): void {
    let placeholder = dom.querySelector(
      '.editor-code-block-placeholder',
    ) as HTMLElement;

    if (this.isEmpty()) {
      if (!placeholder) {
        placeholder = document.createElement('div');
        placeholder.classList.add('editor-code-block-placeholder');
        placeholder.textContent = 'Добавьте код';

        dom.appendChild(placeholder);
      }
    } else if (placeholder) {
      placeholder.remove();

      if (editor) {
        this.addDeleteButton(dom, editor);
      }
    }
  }

  static clone(node: CodeBlockNode): CodeBlockNode {
    return new CodeBlockNode(node.__key);
  }

  static getType(): string {
    return 'code-block-node';
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (element: HTMLElement) => {
        if (element.classList.contains('editor-code-block')) {
          return {
            conversion: $convertCodeBlockElement,
            priority: 1,
          };
        }
        return null;
      },
    };
  }

  updateDOM(_prevNode: unknown, dom: HTMLElement): boolean {
    this.updatePlaceholder(dom);

    if (!this.getTextContent()) {
      this.remove();
    }

    return false;
  }

  insertNewAfter(
    rangeSelection: RangeSelection,
    restoreSelection: boolean,
  ): ParagraphNode {
    const newElement = $createParagraphNode();
    newElement.setTextFormat(rangeSelection.format);
    newElement.setTextStyle(rangeSelection.style);
    const direction = this.getDirection();
    newElement.setDirection(direction);
    newElement.setFormat(this.getFormatType());
    newElement.setStyle(this.getStyle());
    this.insertAfter(newElement, restoreSelection);

    return newElement;
  }

  static importJSON(serializedNode: SerializedCodeBlockNode): CodeBlockNode {
    return $createCodeBlockNode().updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedCodeBlockNode {
    return {
      ...super.exportJSON(),
      type: this.getType(),
      version: 1,
      textFormat: this.getTextFormat(),
      textStyle: this.getTextStyle(),
    };
  }
}
